import { Dispatch } from '@reduxjs/toolkit';
import { Address, Hex, PublicClient } from 'viem';

import { waitForReceiptWithRetry } from 'modules/api/utils/waitForReceiptRetry';
import { KnownDialogs, openDialog } from 'modules/dialogs';

import { isSafeAccount } from './isSafeAccount';

interface WrapOptions {
  dispatch: Dispatch;
  accountAddress: Address;
  publicClient: PublicClient;
  txPromise: Promise<Hex>;
  safeTimeoutMs?: number;
}

const DEFAULT_SAFE_TIMEOUT_MS = 1000;
const ERROR_TIMEOUT_KEY = 'timeout';

export async function handleSafeAppTransaction({
  dispatch,
  accountAddress,
  publicClient,
  txPromise,
  safeTimeoutMs = DEFAULT_SAFE_TIMEOUT_MS,
}: WrapOptions): Promise<Hex | null> {
  const isSafe = await isSafeAccount(accountAddress);

  if (isSafe) {
    try {
      await Promise.race([
        txPromise,
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error(ERROR_TIMEOUT_KEY)), safeTimeoutMs),
        ),
      ]);

      const hash = await txPromise;

      return hash;
    } catch (e) {
      if ((e as Error)?.message === ERROR_TIMEOUT_KEY) {
        dispatch(openDialog({ currentModal: KnownDialogs.safeApp }));

        return null;
      }

      throw e;
    }
  } else {
    const hash = await txPromise;

    await waitForReceiptWithRetry(publicClient, { hash });

    return hash;
  }
}
